/**
 * Proforma list (ADR-0032) — status tabs + cursor-paginated table.
 *
 * Leaner than the invoice list: a proforma is non-fiscal, so there's no
 * receivables KPI strip. Tabs: All / Draft / Finalized / Converted / Expired.
 *
 * Chrome mirrors the invoices list (S11) so the two sales surfaces read as
 * one system: underlined status tabs (brand-spec §13.2), the 36 px search
 * box, and the table-card (paper bg, raised thead, mono numerals, footer
 * with "Showing X–Y of N" + pagination).
 *
 * @see prototype/sales/invoices.html — `.table-card`, `.table-foot`
 * @see features/invoices/list/ — the reference implementation
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Plus, Search as SearchIcon, Upload, X } from "lucide-react";
import { Button, StatusPill, TabsContent, TabsList, TabsRoot, TabsTrigger } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { paiseToInr, formatDate } from "../../../lib/format.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { ImportProformasDialog } from "./import-dialog.tsx";
import {
  proformaStatusLabel,
  proformaStatusTone,
  type ProformaStatus,
} from "../lib/proforma-status.ts";

type TabKey = ProformaStatus | "all";
const TABS: ReadonlyArray<{ value: TabKey; label: string }> = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "finalized", label: "Finalized" },
  { value: "converted", label: "Converted" },
  { value: "expired", label: "Expired" },
];
const PAGE_SIZE = 50;

export function ProformaListPage() {
  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const canCreate = usePermission("invoices.create");
  const cursor = cursorStack.at(-1);

  // Reset pagination when the filter set changes.
  useEffect(() => {
    setCursorStack([]);
  }, [tab, search]);

  const list = trpc.proformas.list.useQuery(
    {
      limit: PAGE_SIZE,
      ...(tab !== "all" ? { status: tab } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const rows = list.data?.items ?? [];
  const counts = list.data?.counts;
  const nextCursor = list.data?.nextCursor;

  const tabCounts = useMemo(
    () =>
      Object.fromEntries(TABS.map(({ value }) => [value, counts ? counts[value] : 0])) as Record<
        TabKey,
        number
      >,
    [counts],
  );

  const pageStart = cursorStack.length * PAGE_SIZE;
  const grandTotal = counts ? counts[tab] : rows.length;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Proformas</h1>
          <p className="mt-0.5 text-sm text-muted">
            Preliminary, non-fiscal quotes. Convert to a tax invoice when the sale is confirmed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate ? (
            <Button
              type="button"
              intent="ghost"
              onClick={() => setImportOpen(true)}
              title="Import proformas from a CSV or XLSX file"
            >
              <Upload size={14} />
              <span>Import</span>
            </Button>
          ) : null}
          <Button asChild>
            <Link to="/sales/proformas/new">
              <Plus size={14} />
              <span>New proforma</span>
            </Link>
          </Button>
        </div>
      </header>
      <ImportProformasDialog open={importOpen} onOpenChange={setImportOpen} />

      <TabsRoot value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList aria-label="Filter by status">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} count={tabCounts[t.value]}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* Each Radix TabsTrigger sets aria-controls to a panel id; the
            filtered table below isn't a Radix panel, so without these the
            references dangle (axe aria-valid-attr-value, critical). Same
            fix as the invoices StatusTabs. */}
        {TABS.map((t) => (
          <TabsContent key={t.value} value={t.value} forceMount className="hidden" aria-hidden />
        ))}
      </TabsRoot>

      {/* Search — h-9 rectangular box matching the invoices filter bar. */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative flex-1 min-w-[240px] max-w-[420px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
            <SearchIcon size={14} aria-hidden />
          </span>
          <input
            type="search"
            aria-label="Search proformas"
            placeholder="Search doc number, customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={[
              "h-9 w-full pl-9 pr-8 rounded-sm border border-line bg-bg text-sm text-ink",
              "placeholder:text-muted",
              "hover:border-line-2",
              "focus:outline-none focus:border-sky-600 focus:bg-paper",
              "focus:shadow-[0_0_0_3px_var(--color-sky-100)]",
              "transition-colors",
            ].join(" ")}
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 grid h-5 w-5 place-items-center rounded-sm text-muted hover:text-ink-2 hover:bg-raised"
            >
              <X size={12} />
            </button>
          ) : null}
        </label>
      </div>

      <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
        {list.isLoading ? (
          <p className="p-6 text-sm text-muted">Loading…</p>
        ) : rows.length === 0 ? (
          search.trim() !== "" || tab !== "all" ? (
            <div className="p-10 text-center">
              <p className="text-sm font-medium text-ink">
                {search.trim() !== "" ? "No proformas match your search." : "Nothing in this tab yet."}
              </p>
              <p className="mt-1 text-sm text-muted">
                {search.trim() !== ""
                  ? "Try a different customer name or PRO number."
                  : "Proformas move into this tab as they progress through the lifecycle."}
              </p>
            </div>
          ) : (
            <div className="p-10 text-center">
              <p className="text-sm font-medium text-ink">No proformas yet</p>
              <p className="mt-1 text-sm text-muted">
                Create a proforma to quote a customer before issuing a tax invoice.
              </p>
              <Button asChild className="mt-4">
                <Link to="/sales/proformas/new">
                  <Plus size={14} />
                  <span>New proforma</span>
                </Link>
              </Button>
            </div>
          )
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[840px] table-fixed text-sm">
                <colgroup>
                  <col className="w-40" />
                  <col />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-32" />
                  <col className="w-32" />
                </colgroup>
                <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-4 py-4 text-left font-medium">Number</th>
                    <th className="px-4 py-4 text-left font-medium">Customer</th>
                    <th className="px-3 py-4 text-left font-medium">Date</th>
                    <th className="px-3 py-4 text-left font-medium">Valid until</th>
                    <th className="px-3 py-4 text-right font-medium">Amount</th>
                    <th className="px-3 py-4 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-line hover:bg-raised">
                      <td className="px-4 py-3 font-mono font-semibold text-ink-2">
                        <Link
                          to="/sales/proformas/$proformaId"
                          params={{ proformaId: row.id }}
                          className="block truncate hover:underline"
                        >
                          {row.docNumber || <span className="text-muted">— draft —</span>}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-ink">{row.customerName}</span>
                        {row.customerCity ? (
                          <span className="text-muted"> · {row.customerCity}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 font-mono text-muted">{formatDate(row.issuedOn)}</td>
                      <td className="px-3 py-3 font-mono text-muted">
                        {row.expiresOn ? formatDate(row.expiresOn) : "—"}
                      </td>
                      <td className="px-3 py-3 text-right font-mono font-semibold text-ink">
                        {row.currencyCode !== "INR" && row.foreignAmountMinor
                          ? `${row.currencyCode} ${(Number(row.foreignAmountMinor) / 100).toLocaleString("en-IN")}`
                          : paiseToInr(row.totalPaise)}
                      </td>
                      <td className="px-3 py-3">
                        <StatusPill tone={proformaStatusTone(row.status)} size="sm" dot>
                          {proformaStatusLabel(row.status)}
                        </StatusPill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer — "Showing X–Y of N" + cursor pagination, same chrome
                as the invoices table foot. */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
              <span>
                Showing{" "}
                <strong className="font-mono tabular-nums font-bold text-ink">
                  {`${pageStart + 1}–${pageStart + rows.length}`}
                </strong>{" "}
                of{" "}
                <strong className="font-mono tabular-nums font-bold text-ink">{grandTotal}</strong>{" "}
                · sorted by issue date (newest first)
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={cursorStack.length === 0}
                  aria-label="Previous page"
                  onClick={() => setCursorStack((s) => s.slice(0, -1))}
                  className="grid h-7 w-7 place-items-center rounded-sm border border-line bg-paper text-muted hover:text-ink-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft size={12} strokeWidth={2.4} />
                </button>
                <button
                  type="button"
                  disabled={!nextCursor}
                  aria-label="Next page"
                  onClick={() => nextCursor && setCursorStack((s) => [...s, nextCursor])}
                  className="grid h-7 w-7 place-items-center rounded-sm border border-line bg-paper text-muted hover:text-ink-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronRight size={12} strokeWidth={2.4} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
